import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Clock, AlertTriangle, Loader2 } from "lucide-react";
import api from "../lib/api";

/**
 * Iter-30.16 — Heures réelles de formation par apprenant/manager.
 * Affiche le temps réel passé (via heartbeats 30s), les points/minute
 * et flagge les "speeders" potentiels.
 *
 * Endpoint backend : GET /api/admin/learning-time/overview
 */
export default function LearningTimeOverview() {
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [error, setError] = useState(null);
    const [filter, setFilter] = useState("all"); // all | learner | manager

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/admin/learning-time/overview");
                if (cancelled) return;
                setRows(data.rows || []);
            } catch (e) {
                if (!cancelled) setError("Impossible de charger les heures de formation");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const sorted = useMemo(() => {
        const filtered = filter === "all" ? rows : rows.filter((r) => r.role === filter);
        return [...filtered].sort((a, b) => (b.total_seconds || 0) - (a.total_seconds || 0));
    }, [rows, filter]);

    const formatHours = (secs) => {
        if (!secs || secs < 60) return "—";
        const h = Math.floor(secs / 3600);
        const m = Math.floor((secs % 3600) / 60);
        if (h === 0) return `${m} min`;
        return `${h}h ${m.toString().padStart(2, "0")}`;
    };

    if (loading) {
        return (
            <div className="glass-card rounded-3xl p-6 sm:p-8 flex items-center gap-3 text-white/60">
                <Loader2 className="w-5 h-5 animate-spin text-shariti-orange" />
                <span className="text-sm">Chargement des heures de formation…</span>
            </div>
        );
    }
    if (error) {
        return (
            <div className="glass-card rounded-3xl p-6 sm:p-8 text-red-300 text-sm">
                {error}
            </div>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="glass-card rounded-3xl p-6 sm:p-8"
            data-testid="learning-time-overview"
        >
            <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
                <div>
                    <p className="text-xs font-thin tracking-[0.25em] uppercase text-shariti-orange-light mb-1 flex items-center gap-2">
                        <Clock className="w-3.5 h-3.5" />
                        Heures réelles de formation
                    </p>
                    <h3 className="text-xl font-extrabold text-white">
                        Suivi temps passé (apprenants & managers)
                    </h3>
                    <p className="text-xs text-white/45 mt-1 max-w-xl">
                        Heartbeat 30s pendant qu&apos;une leçon est ouverte. Détecte les profils
                        {" "}&laquo;&nbsp;speeders&nbsp;&raquo; (pts/min trop élevé = clic zappant les contenus).
                    </p>
                </div>
                <div className="flex gap-1.5">
                    {[
                        { v: "all", l: "Tous" },
                        { v: "learner", l: "Apprenants" },
                        { v: "manager", l: "Managers" },
                    ].map((o) => (
                        <button
                            key={o.v}
                            onClick={() => setFilter(o.v)}
                            data-testid={`learning-time-filter-${o.v}`}
                            className={`px-2.5 py-1 rounded-full text-[11px] font-extrabold uppercase tracking-widest transition-all ${
                                filter === o.v
                                    ? "bg-shariti-orange text-white"
                                    : "bg-white/5 text-white/60 hover:bg-white/10"
                            }`}
                        >
                            {o.l}
                        </button>
                    ))}
                </div>
            </div>

            {sorted.length === 0 ? (
                <p className="text-sm text-white/50">Aucune donnée encore — les heartbeats commencent quand les apprenants ouvrent une leçon.</p>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="learning-time-table">
                        <thead>
                            <tr className="text-[10px] font-thin tracking-[0.22em] uppercase text-white/40 border-b border-white/5">
                                <th className="py-2 pr-3 text-left">Apprenant</th>
                                <th className="py-2 pr-3 text-right">Heures réelles</th>
                                <th className="py-2 pr-3 text-right">Points</th>
                                <th className="py-2 pr-3 text-right">Pts / min</th>
                                <th className="py-2 pl-3 text-right">Signal</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.map((r) => {
                                const isSpeeder = r.is_likely_speeder;
                                return (
                                    <tr
                                        key={r.user_id}
                                        data-testid={`learning-time-row-${r.user_id}`}
                                        className="border-b border-white/5 hover:bg-white/[0.03] transition-colors"
                                    >
                                        <td className="py-2.5 pr-3">
                                            <div className="flex items-center gap-2.5">
                                                {r.avatar ? (
                                                    <img
                                                        src={r.avatar}
                                                        alt=""
                                                        className="w-7 h-7 rounded-lg object-cover"
                                                    />
                                                ) : (
                                                    <div className="w-7 h-7 rounded-lg bg-shariti-orange/20 flex items-center justify-center text-xs font-bold text-shariti-orange">
                                                        {(r.name || "?")[0]}
                                                    </div>
                                                )}
                                                <div className="min-w-0">
                                                    <p className="text-sm font-bold text-white truncate">{r.name}</p>
                                                    <p className="text-[10px] text-white/40 truncate">{r.email} · {r.role}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="py-2.5 pr-3 text-right tabular-nums">
                                            <span className="text-white font-bold">{formatHours(r.total_seconds)}</span>
                                            {r.total_seconds < 60 && (
                                                <p className="text-[9px] text-white/30">aucune session</p>
                                            )}
                                        </td>
                                        <td className="py-2.5 pr-3 text-right tabular-nums font-extrabold text-shariti-orange">
                                            {r.points || 0}
                                        </td>
                                        <td className="py-2.5 pr-3 text-right tabular-nums">
                                            {r.points_per_minute > 0 ? (
                                                <span className={isSpeeder ? "text-red-400 font-bold" : "text-white/70"}>
                                                    {r.points_per_minute}
                                                </span>
                                            ) : (
                                                <span className="text-white/30">—</span>
                                            )}
                                        </td>
                                        <td className="py-2.5 pl-3 text-right">
                                            {isSpeeder ? (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/15 text-red-300 text-[10px] font-extrabold tracking-wider">
                                                    <AlertTriangle className="w-3 h-3" />
                                                    Speeder ?
                                                </span>
                                            ) : (
                                                <span className="text-white/30 text-[10px]">OK</span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </motion.div>
    );
}
